import {computed, NgZone, signal, untracked} from '@angular/core';
import {scrollParentOf} from '../../_shared/dom/scroll-parent';

/**
 * The pinned header clone's sticky offset, in px, mirrored into `pinned-table.css`. Negative because a sticky
 * box inside the shell's padded scroll container comes to rest at (container top + its 32px padding-top + this),
 * measured identically in Chromium and WebKit — so -20 leaves a 12px gap under the navbar. It doubles as the
 * hand-over line the real header is compared against, which is what makes the swap from real to clone seamless.
 *
 * Not a magic number: it IS `app.component.html`'s `py-8` on the office shell's `#relative` container, minus a
 * 12px breathing gap. If that padding changes, this changes with it — and so does `.cq-pinned-scroll`'s
 * `bottom: -32px`, which cancels the same padding at the other end.
 */
export const PINNED_HEAD_TOP_PX = -20;

/**
 * The six DOM nodes one pinned table is made of. Every one is optional: the whole construction lives inside a
 * loading / error / empty `@if` chain on every screen that uses it, so the parts come and go together and the
 * controller has to survive their absence rather than assume them.
 *
 * @see PinnedTableController for how a screen supplies them.
 */
export interface PinnedTableParts {
  /** The `overflow-x-auto` scroll container around the real table — the single source of scroll position. */
  wrapper?: HTMLElement;
  /** The REAL `<thead>`: the trigger for handing over to the clone (never the clone's own head). */
  realHead?: HTMLElement;
  /** The REAL header `<tr>`: the width source every clone column is measured from. */
  realHeadRow?: HTMLElement;
  /** The REAL `<table>`: what a `ResizeObserver` watches, because it is what actually changes width. */
  realTable?: HTMLElement;
  /** The header clone's clipping box (`overflow: hidden`), whose `scrollLeft` mirrors the wrapper's. */
  headViewport?: HTMLElement;
  /** The scrollbar clone's own `overflow-x: auto` box — a real control, so its `scrollLeft` is two-way. */
  scrollViewport?: HTMLElement;
}

/**
 * The mechanics behind the house "data table with a floating header" pattern: a presentational header clone and
 * a presentational horizontal scrollbar that stick against the PAGE while the real table scrolls inside its own
 * wrapper, plus the frozen identity column's scroll-driven edge.
 *
 * Extracted verbatim from `employees.component.ts` (#389 header clone, #458 scrollbar clone) when the
 * Pflicht-Kontakte screen (#558) needed the same behaviour, so the two screens share ONE implementation of a
 * construction whose every line is a measured workaround. The *markup* is deliberately NOT shared — a clone's
 * header cells are whatever that screen's header cells are, and a generic component would either lose the
 * clone's live sort buttons or reduce the header to a data array. What is shared is everything a second copy
 * would get subtly wrong: the measurement, the two-way scroll sync and its feedback guard, and the hand-over
 * lines. The matching stylesheet is `pinned-table.css`, pulled in by both screens via `styleUrls`.
 *
 * Why the clones exist at all (all three of these are measured, not theoretical):
 *
 * 1. `overflow-x: auto` on the wrapper forces `overflow-y` to compute to `auto` as well (CSS Overflow 3), so
 *    the wrapper is a scroll container on BOTH axes and anything `sticky` inside it pins to the WRAPPER, never
 *    to the page. A naive sticky `<thead>` simply scrolls away with the page. Neither `overflow-y: clip` nor
 *    `hidden` escapes it. Capping the wrapper's height WOULD pin it, at the price of a second vertical
 *    scrollbar — rejected in #389.
 * 2. The same rule strands the native horizontal scrollbar at the bottom of the whole table, far below the
 *    fold on a realistic roster, which is why the bar is a clone too (#458).
 * 3. An `IntersectionObserver` on either boundary looks right and never fires in this scrollport — measured
 *    with the header 867px above the viewport and the clone correctly stuck: the callback had still never run.
 *    Both hand-overs are therefore plain passive scroll listeners comparing against the scrollport's own edge.
 *
 * Usage — the screen owns the markup and the `viewChild`s, the controller owns the behaviour:
 *
 * ```ts
 * protected readonly pinned = new PinnedTableController(inject(NgZone), () => ({
 *   wrapper: this.tableWrapper()?.nativeElement,
 *   realHead: this.realHead()?.nativeElement,
 *   realHeadRow: this.realHeadRow()?.nativeElement,
 *   realTable: this.realTable()?.nativeElement,
 *   headViewport: this.headViewport()?.nativeElement,
 *   scrollViewport: this.scrollViewport()?.nativeElement
 * }));
 *
 * constructor() {
 *   effect(onCleanup => this.pinned.bind(onCleanup));
 * }
 * ```
 *
 * The parts accessor is read INSIDE the effect, so it is what makes the effect re-run when the table appears or
 * disappears — never resolve the elements outside it.
 */
export class PinnedTableController {
  /**
   * Whether the table is scrolled horizontally at all. Drives the frozen column's edge, which must only appear
   * once there really is content sliding underneath it (#389) — at rest a permanent hairline is a stray rule.
   */
  readonly scrolledX = signal(false);

  /**
   * Whether the presentational header clone is showing — i.e. the real header has passed the line the clone
   * rests on. Never both at once: at the top of the page the real header is the only one on screen.
   */
  readonly headerPinned = signal(false);

  /**
   * Whether the pinned horizontal scrollbar clone is showing (#458). True when both hold:
   *
   * 1. the table really overflows sideways — with no overflow there is nothing to scroll and a bar would be a
   *    stray decoration; and
   * 2. the table is on screen at all.
   *
   * This clone is the ONLY horizontal control for the table: the wrapper's own native bar is suppressed by
   * `.cq-table-wrapper`. That is deliberate, and it replaced a first cut in which both existed — two bars for
   * one box meant the control moved and changed appearance depending on how far down the page you were.
   */
  readonly scrollbarPinned = signal(false);

  /** Per-column widths measured off the REAL header, and their total. A hidden column measures 0 and stays 0. */
  readonly columnWidths = signal<number[]>([]);

  readonly pinnedHeadWidth = computed(() =>
    this.columnWidths().reduce((total, width) => total + width, 0));

  /**
   * @param zone the host component's `NgZone`. Every listener here is passive and registered OUTSIDE it — a
   *        scroll handler that schedules change detection on every frame of a fling is exactly what makes a
   *        table feel heavy — and only the rare state transition re-enters it.
   * @param parts resolves the six nodes; called inside {@link bind}, so reading `viewChild` signals in it is
   *        what makes the binding effect track them.
   */
  constructor(
    private readonly zone: NgZone,
    private readonly parts: () => PinnedTableParts
  ) {
  }

  /**
   * Bind every listener and observer for one appearance of the table, and register their teardown.
   *
   * Call it as the whole body of an `effect(onCleanup => controller.bind(onCleanup))`: Angular re-runs that
   * effect whenever the parts change identity (the `@if` chain swapping the table in or out) and runs the
   * registered cleanup both on re-run and on the component's `DestroyRef`.
   *
   * @param onCleanup the effect's own cleanup registration.
   */
  bind(onCleanup: (register: () => void) => void): void {
    const {wrapper, realHead, realHeadRow, realTable, headViewport, scrollViewport} = this.parts();

    if (!wrapper) {
      // The table is not rendered — reset, or the edge would still be on when it comes back at scrollLeft 0,
      // and the pinned scrollbar would hang over the loading / error / empty panel that replaced the table.
      untracked(() => {
        this.scrolledX.set(false);
        this.headerPinned.set(false);
        this.scrollbarPinned.set(false);
      });
      return;
    }

    // The FEEDBACK GUARD for the two-way scrollbar sync (#458). Assigning scrollLeft queues a scroll event on
    // the box written to, so without a guard each side would echo the other's write forever. The flag is
    // released on the next animation frame, which is late enough by construction: the event loop dispatches
    // scroll events in the "update the rendering" step BEFORE it runs requestAnimationFrame callbacks, so the
    // echo is always delivered while the flag is still up and is swallowed there.
    //
    // Deliberately NOT a value comparison ("skip if the two already agree"). The wrapper and the clone round
    // sub-pixel offsets independently, and rubber-band overscroll drives one past a limit the other clamps at,
    // so the two values legitimately differ at exactly the moments the sync matters — a comparison guard both
    // leaks echoes and drops real updates.
    let syncing = false;
    let syncFrame = 0;
    const beginSync = () => {
      syncing = true;
      if (syncFrame) cancelAnimationFrame(syncFrame);
      syncFrame = requestAnimationFrame(() => {
        syncing = false;
        syncFrame = 0;
      });
    };

    // Everything that has to follow the wrapper's horizontal offset EXCEPT the scrollbar clone, whose write is
    // guarded separately. Factored out because the reverse direction (a drag on the clone) suppresses the
    // wrapper's own scroll handler via the flag above and must therefore do this work itself, or the frozen
    // column's edge and the pinned header would sit frozen while the user drags the bar.
    const mirror = () => {
      if (headViewport) headViewport.scrollLeft = wrapper.scrollLeft;
      const scrolled = wrapper.scrollLeft > 0;
      if (scrolled !== untracked(this.scrolledX)) this.zone.run(() => this.scrolledX.set(scrolled));
    };

    // wrapper -> clones.
    const update = () => {
      mirror();
      if (scrollViewport && !syncing) {
        beginSync();
        scrollViewport.scrollLeft = wrapper.scrollLeft;
      }
    };

    // clone -> wrapper. This direction is what the whole feature is for: the pinned bar is a control, not a
    // read-out, so dragging it must move the table.
    const updateFromScrollBar = () => {
      if (!scrollViewport || syncing) return;
      beginSync();
      wrapper.scrollLeft = scrollViewport.scrollLeft;
      mirror();
    };

    update();
    // untracked: measureColumnWidths READS columnWidths (to stay idempotent) and WRITES it. Without this the
    // effect would depend on a signal it sets, re-run on every measurement, and tear down the observers it had
    // just created — which is exactly why the clone never appeared in the first cut of #389.
    const syncWidths = () => this.zone.run(() => untracked(() => this.measureColumnWidths(realHeadRow)));
    syncWidths();

    // The clone appears exactly when the real header passes the pin line, so the two are never both on screen.
    const stopWatchingHead = this.watchRealHead(realHead, headViewport);
    // Likewise for the bottom edge: the scrollbar clone appears exactly when the real bar leaves the scrollport.
    const scrollBarWatch = this.watchWrapperBottom(wrapper, scrollViewport);
    // Re-checking the scrollbar clone here is not belt-and-braces. The table resizing is precisely what changes
    // whether it overflows at all (a responsive column appearing, the data itself), and its height changing is
    // what moves the wrapper's bottom edge across the fold — neither shows up as a window resize.
    const widthObserver = realTable
      ? new ResizeObserver(() => {
          syncWidths();
          scrollBarWatch?.refresh();
        })
      : null;

    this.zone.runOutsideAngular(() => {
      wrapper.addEventListener('scroll', update, {passive: true});
      scrollViewport?.addEventListener('scroll', updateFromScrollBar, {passive: true});
      // A viewport change can make the table stop (or start) overflowing, which silently changes scrollLeft —
      // and it can cross a breakpoint, which changes WHICH columns exist. Re-measure on both counts rather
      // than relying on the ResizeObserver alone: the table is min-w-full, so its own border box can stay the
      // same width while its column set changes underneath.
      window.addEventListener('resize', update, {passive: true});
      window.addEventListener('resize', syncWidths, {passive: true});
      if (realTable && widthObserver) widthObserver.observe(realTable);
    });

    onCleanup(() => {
      wrapper.removeEventListener('scroll', update);
      scrollViewport?.removeEventListener('scroll', updateFromScrollBar);
      window.removeEventListener('resize', update);
      window.removeEventListener('resize', syncWidths);
      widthObserver?.disconnect();
      stopWatchingHead?.();
      scrollBarWatch?.stop();
      if (syncFrame) cancelAnimationFrame(syncFrame);
      untracked(() => {
        this.headerPinned.set(false);
        this.scrollbarPinned.set(false);
      });
    });
  }

  /**
   * Copy the REAL header's rendered column widths onto the clone. The clone is `table-layout: fixed`, so those
   * col widths are honoured exactly; the real table stays auto-layout and keeps deciding what the widths ARE.
   * Nothing here is authored or guessed, which is why the two cannot drift.
   */
  private measureColumnWidths(row: HTMLElement | undefined): void {
    if (!row || row.getClientRects().length === 0) return;
    // VISIBLE cells only, and that filter is the whole correctness of this method. A `display: none` cell
    // occupies no column slot at all, so a <col> list built over ALL cells maps its Nth entry onto the Nth
    // VISIBLE column: below `lg` the employees table hides five columns, "Stunden" inherited the 0 width of
    // hidden "Letzter Dienst", "Schichten" inherited Stunden's width AND its x, and the two labels drew on top
    // of each other (reported at 800px as "SCUHNDETEN"). Both sides hide the same columns via the same
    // responsive classes, so filtering to visible cells here makes the two index spaces line up at every
    // breakpoint.
    //
    // Full sub-pixel precision, deliberately un-rounded: rounding each column to 2dp left the fixed layout a
    // fraction of a pixel to redistribute, which showed up as up to 0.08px of drift on the last column.
    const widths = Array.from(row.children)
      .filter(cell => cell.getClientRects().length > 0)
      .map(cell => cell.getBoundingClientRect().width);
    const current = this.columnWidths();
    // Idempotent: an unchanged measurement must not churn the signal (the ResizeObserver fires generously).
    // Compared with a sub-pixel tolerance so layout jitter cannot loop the observer.
    if (widths.length === current.length && widths.every((w, i) => Math.abs(w - current[i]) < 0.01)) return;
    this.columnWidths.set(widths);
  }

  /**
   * Show the header clone only once the real header has passed the line the clone rests on.
   *
   * Deliberately a plain scroll listener rather than an IntersectionObserver — see this class's own note on why
   * the observer version was measured dead in this scrollport.
   *
   * @param head the real `<thead>`.
   * @param anchor the clone's viewport, used to resolve the scrollport. Resolving it from the HEADER instead
   *        finds the table wrapper first — whose `overflow-y` computes to `auto` (the whole reason this clone
   *        exists) and which never scrolls vertically — so the hand-over could never fire.
   * @returns an unbind function, or `null` when there is no header to watch.
   */
  private watchRealHead(head: HTMLElement | undefined, anchor: HTMLElement | undefined): (() => void) | null {
    if (!head) return null;
    const scroller = scrollParentOf(anchor ?? head);
    // The scroll SOURCE and the coordinate origin differ: when the document scrolls, listen on window and
    // measure against the viewport top; when the shell's container scrolls, listen on it and measure against
    // its own top edge.
    const documentScrolls = !(scroller instanceof HTMLElement) || scroller === document.scrollingElement;
    const source: EventTarget = documentScrolls ? window : (scroller as HTMLElement);

    const update = () => {
      const originTop = documentScrolls ? 0 : (scroller as HTMLElement).getBoundingClientRect().top;
      // Hand over when the real header's TOP reaches the line the clone rests on. The two are the same height,
      // so at that instant they occupy the same box and the swap is invisible. Comparing the header's BOTTOM
      // instead leaves a gap the height of the header in which NEITHER is on screen (measured: 20px of nothing).
      const pinned = head.getBoundingClientRect().top <= originTop + PINNED_HEAD_TOP_PX;
      if (pinned === untracked(this.headerPinned)) return;
      this.zone.run(() => this.headerPinned.set(pinned));
    };

    update();
    this.zone.runOutsideAngular(() => {
      source.addEventListener('scroll', update, {passive: true});
      window.addEventListener('resize', update, {passive: true});
    });
    return () => {
      source.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }

  /**
   * Show the pinned scrollbar clone only while the REAL one is both needed and out of reach — see
   * {@link scrollbarPinned} for the two conditions.
   *
   * The wrapper's bottom edge stands in for the real scrollbar because that is where the browser paints it.
   *
   * @param wrapper the table's scroll container.
   * @param anchor the scrollbar clone's viewport, used to resolve the scrollport for the same reason
   *        {@link watchRealHead}'s does.
   * @returns `refresh` to re-evaluate after a layout change no scroll or resize event announces (the table's
   *          own `ResizeObserver`), and `stop` to unbind.
   */
  private watchWrapperBottom(
    wrapper: HTMLElement,
    anchor: HTMLElement | undefined
  ): { refresh: () => void; stop: () => void } {
    // scrollParentOf skips the element it is given, so the wrapper is a sound fallback anchor.
    const scroller = scrollParentOf(anchor ?? wrapper);
    const documentScrolls = !(scroller instanceof HTMLElement) || scroller === document.scrollingElement;
    const source: EventTarget = documentScrolls ? window : (scroller as HTMLElement);

    const update = () => {
      // A 1px tolerance on both tests, not a fudge: scrollWidth/clientWidth are rounded integers that can differ
      // by one on a table whose columns are sub-pixel wide, and the bottom edge jitters by a fraction during a
      // fling. Without it the clone flickers on and off at rest.
      const overflows = wrapper.scrollWidth - wrapper.clientWidth > 1;
      const originBottom = documentScrolls
        ? (window.innerHeight || document.documentElement.clientHeight)
        : (scroller as HTMLElement).getBoundingClientRect().bottom;
      const rect = wrapper.getBoundingClientRect();
      // The clone is now the ONLY horizontal control: the wrapper's native bar is suppressed in the stylesheet.
      // So the test is simply "is there something to scroll, and is the table in front of the user" — NOT the
      // earlier "…and has the real bar fallen off screen". Hiding the native bar while gating the clone on that
      // would leave a stretch of the page, at the end of a long list, with no horizontal control at all.
      //
      // The top edge is still tested, and it is not redundant. A sticky box cannot leave its containing block,
      // which here is the table's own wrapper box — so while the table is entirely BELOW the fold the clone is
      // clamped to the table's top edge and parks off-screen, and claiming "pinned" there would be a lie the
      // CSS quietly contradicts (measured: signal on, box at y=532 in a 480px viewport).
      //
      // There is deliberately NO bottom test any more. That same containing-block clamp is what makes it safe:
      // once the table's own bottom rises above the viewport, the clone stops at that bottom edge instead of
      // floating on at the window edge, so it comes to rest exactly where the native bar used to be.
      const pinned = overflows && rect.top < originBottom && rect.bottom > 0;
      if (pinned === untracked(this.scrollbarPinned)) return;
      this.zone.run(() => this.scrollbarPinned.set(pinned));
    };

    update();
    this.zone.runOutsideAngular(() => {
      source.addEventListener('scroll', update, {passive: true});
      window.addEventListener('resize', update, {passive: true});
    });
    return {
      refresh: update,
      stop: () => {
        source.removeEventListener('scroll', update);
        window.removeEventListener('resize', update);
      }
    };
  }
}
